/** A reusable template for creating checkouts with predefined line items, coupons, and customization settings. */
export interface CheckoutTemplate {
  /**
   * Checkout template ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Status of the checkout template.
   *
   * When `status` is `INACTIVE` checkouts will not be created with this template `id`. Instead, the endpoint will redirect to the domain site.
   *
   * Default: `ACTIVE`
   */
  status?: StatusWithLiterals;
  /** Custom settings to apply to the checkout page created from this template. */
  customization?: CheckoutCustomization;
  /**
   * Catalog line items.
   *
   * Max: 300 items
   * @maxSize 300
   */
  lineItems?: V1LineItem[];
  /**
   * Custom line items. Custom line items don't trigger the Catalog service plugin.
   *
   * Max: 300 items
   * @maxSize 300
   */
  customLineItems?: CustomLineItem[];
  /**
   * Coupon code.
   *
   * Note that a checkout can only hold one `couponCode` at a time. If an additional `couponCode` is added, it will override the existing `couponCode`.
   * For additional information, see the Coupons API.
   */
  couponCode?: string | null;
  /**
   * Reference IDs for the app and component providing custom checkout page content.
   *
   * To access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions".
   * Learn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).
   */
  customContentReference?: CustomContentReference;
  /**
   * References to an external app and resource associated with the checkout.
   * Used for integration and tracking across different platforms.
   * @immutable
   */
  externalReference?: ExternalReference;
}

export enum Status {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

/** @enumType */
export type StatusWithLiterals = Status | 'ACTIVE' | 'INACTIVE';

export interface CheckoutCustomization {
  /** Web client customizations. These customizations only apply to the standard Wix checkout page. */
  webClient?: WebClientCustomization;
  /**
   * Whether to lock the ability to add or remove a gift card.
   *
   * Default: `false`
   */
  lockedGiftCard?: boolean;
  /**
   * Whether to lock the ability to apply or remove a coupon code.
   *
   * Default: `false`
   */
  lockedCoupon?: boolean;
  /**
   * Whether to disable manual payment option for this checkout.
   *
   * Default: `false`
   */
  disabledManualPayment?: boolean;
}

export interface WebClientCustomization {
  /**
   * Whether to disable the "Continue shopping" button.
   *
   * Default: `false`
   */
  disabledContinueShoppingButton?: boolean;
  /**
   * Whether to disable policy agreement checkout in the checkout page
   *
   * Default: `false`
   */
  disabledPolicyAgreementCheckbox?: boolean;
}

export interface V1LineItem {
  /**
   * Item quantity.
   *
   * Min: `1` <br />
   * Max: `100000`
   * @min 1
   * @max 100000
   */
  quantity?: number;
  /** Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin. */
  catalogReference?: CatalogReference;
  /** Overriding values for catalog item properties. */
  catalogOverrideFields?: CatalogOverrideFields;
}

/** Used for grouping line items. Sent when an item is added to a cart, checkout, or order. */
export interface CatalogReference {
  /**
   * ID of the item within the catalog it belongs to.
   * @minLength 1
   * @maxLength 36
   */
  catalogItemId?: string;
  /**
   * ID of the app providing the catalog.
   *
   * You can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   *
   * For items from Wix catalogs, the following values always apply:
   * + Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`
   * + Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * + Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`
   * @minLength 1
   */
  appId?: string;
  /**
   * Additional item details in `key:value` pairs.
   *
   * Use this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.
   *
   * For Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)
   * or [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).
   */
  options?: Record<string, any> | null;
}

export interface CatalogOverrideFields {
  /** Item name. */
  productName?: ProductName;
  /**
   * Item price **after** discounts.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  price?: string | null;
  /**
   * Item price **before** discounts.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  fullPrice?: string | null;
  /**
   * Item description lines. Used when displaying the line item to customers.
   * @maxSize 20
   */
  descriptionLines?: DescriptionLine[];
  /** Physical properties of the item. */
  physicalProperties?: PhysicalProperties;
  /** Item image. */
  image?: Image;
  /** Payment method selected for the item. */
  paymentOption?: PaymentOption;
  /**
   * Only eligible for catalog items with `lineItem.paymentOption.value` type of `DEPOSIT_ONLINE`.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lte:999999999, maxScale:3 }
   */
  depositAmount?: string | null;
  /**
   * Whether to save the payment method on the order.
   *
   * Default: `false`
   */
  savePaymentMethod?: boolean | null;
  /**
   * ID of the app managing the inventory.
   * @format GUID
   */
  inventoryAppId?: string | null;
}

export interface ProductName {
  /**
   * __Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   *
   * Min: 1 character.
   * Max: 200 characters.
   * @minLength 1
   * @maxLength 200
   */
  original?: string;
  /**
   * Item name translated into the buyer's language.
   *
   * Min: 1 character.
   * Max: 400 characters.
   * Default: Same as `original`.
   * @minLength 1
   * @maxLength 400
   */
  translated?: string | null;
}

export interface DescriptionLine
  extends DescriptionLineValueOneOf,
    DescriptionLineDescriptionLineValueOneOf {
  /** Description line plain text value. */
  plainText?: PlainTextValue;
  /** Description line color value. */
  colorInfo?: Color;
  /** Description line name. */
  name?: DescriptionLineName;
}

/** @oneof */
export interface DescriptionLineValueOneOf {
  /** Description line plain text value. */
  plainText?: PlainTextValue;
  /** Description line color value. */
  colorInfo?: Color;
}

/** @oneof */
export interface DescriptionLineDescriptionLineValueOneOf {}

export interface DescriptionLineName {
  /**
   * Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @maxLength 100
   */
  original?: string;
  /**
   * Description line name translated into the buyer's language.
   *
   * Default: Same as `original`.
   * @maxLength 200
   */
  translated?: string | null;
}

export interface PlainTextValue {
  /**
   * Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @maxLength 600
   */
  original?: string;
  /**
   * Description line plain text value translated into the buyer's language.
   *
   * Default: Same as `original`.
   * @maxLength 600
   */
  translated?: string | null;
}

export interface Color {
  /**
   * Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @maxLength 500
   */
  original?: string;
  /**
   * Description line color name translated into the buyer's language.
   *
   * Default: Same as `original`.
   * @maxLength 500
   */
  translated?: string | null;
  /** HEX or RGB color code for display. */
  code?: string | null;
}

export enum DescriptionLineType {
  /** Unrecognized type. */
  UNRECOGNISED = 'UNRECOGNISED',
  /** Plain text type. */
  PLAIN_TEXT = 'PLAIN_TEXT',
  /** Color type. */
  COLOR = 'COLOR',
}

/** @enumType */
export type DescriptionLineTypeWithLiterals =
  | DescriptionLineType
  | 'UNRECOGNISED'
  | 'PLAIN_TEXT'
  | 'COLOR';

export interface PhysicalProperties {
  /** Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request. */
  weight?: number | null;
  /**
   * Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).
   * @maxLength 40
   */
  sku?: string | null;
  /** Whether this line item is shippable. */
  shippable?: boolean;
}

export interface Dimensions {
  /**
   * Length. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  length?: string | null;
  /**
   * Width. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  width?: string | null;
  /**
   * Height. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  height?: string | null;
  /** Unit of measurement for dimensions (length, width, height). */
  unit?: DimensionsUnitWithLiterals;
}

export enum DimensionsUnit {
  UNKNOWN_DIMENSIONS_UNIT = 'UNKNOWN_DIMENSIONS_UNIT',
  /** Millimeters. */
  MM = 'MM',
  /** Centimeters. */
  CM = 'CM',
  /** Meters. */
  M = 'M',
  /** Inches. */
  IN = 'IN',
  /** Feet. */
  FT = 'FT',
  /** Yards. */
  YD = 'YD',
}

/** @enumType */
export type DimensionsUnitWithLiterals =
  | DimensionsUnit
  | 'UNKNOWN_DIMENSIONS_UNIT'
  | 'MM'
  | 'CM'
  | 'M'
  | 'IN'
  | 'FT'
  | 'YD';

export interface Image {
  /** WixMedia image ID. */
  id?: string;
  /** Original image height. */
  height?: number;
  /** Original image width. */
  width?: number;
  /** Image alt text. Optional. */
  altText?: string | null;
  /** Image filename. Optional. */
  filename?: string | null;
}

export interface PaymentOption {
  /**
   * Type of selected payment option for current item.
   *
   * Default: `FULL_PAYMENT_ONLINE`
   */
  value?: PaymentOptionTypeWithLiterals;
}

/** Type of selected payment option for catalog item */
export enum PaymentOptionType {
  /** The entire payment for this item happens as part of the checkout. */
  FULL_PAYMENT_ONLINE = 'FULL_PAYMENT_ONLINE',
  /** The entire payment for this item happens after checkout. For example, when using cash, check, or other offline payment methods. */
  FULL_PAYMENT_OFFLINE = 'FULL_PAYMENT_OFFLINE',
  /** Payment for this item is done by charging a membership. Any remaining amount not covered by the membership, such as item modifiers, is paid online. */
  MEMBERSHIP = 'MEMBERSHIP',
  /** Partial payment to be paid upfront during checkout. The initial amount to be paid for each line item is specified in `depositAmount`. */
  DEPOSIT_ONLINE = 'DEPOSIT_ONLINE',
  /** Payment for this item can only be done by charging a membership and must be manually redeemed in the dashboard by the site admin. When selected, `price` is `0`. */
  MEMBERSHIP_OFFLINE = 'MEMBERSHIP_OFFLINE',
  /**
   * Item price is charged to online membership. Any remaining amount not covered by the membership, such as item modifiers, is paid offline.
   * @documentationMaturity preview
   */
  MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER = 'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER',
}

/** @enumType */
export type PaymentOptionTypeWithLiterals =
  | PaymentOptionType
  | 'FULL_PAYMENT_ONLINE'
  | 'FULL_PAYMENT_OFFLINE'
  | 'MEMBERSHIP'
  | 'DEPOSIT_ONLINE'
  | 'MEMBERSHIP_OFFLINE'
  | 'MEMBERSHIP_ONLINE_WITH_OFFLINE_REMAINDER';

export interface CustomLineItem {
  /**
   * Custom line item quantity.
   *
   * Min: `1`
   * Max: `100000`
   * @min 1
   * @max 100000
   */
  quantity?: number;
  /**
   * Custom line item price.
   *
   * Must be a number or a decimal without symbols.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  price?: string;
  /** Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67". */
  priceDescription?: PriceDescription;
  /**
   * Custom line item description lines. Used for display purposes for the cart, checkout and order.
   * @maxSize 10
   */
  descriptionLines?: DescriptionLine[];
  /** Custom line item's media. */
  media?: Image;
  /**
   * Deprecated. Custom line item ID.
   *
   * To maintain a unique value for a custom line item across multiple carts and orders, pass `catalogReference` instead.
   * Learn more about [custom items with catalog reference](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/catalog-and-custom-items#custom-item-with-catalog-reference).
   * @format GUID
   * @readonly
   * @deprecated Deprecated. Custom line item ID.
   *
   * To maintain a unique value for a custom line item across multiple carts and orders, pass `catalogReference` instead.
   * Learn more about [custom items with catalog reference](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/catalogs/catalog-and-custom-items#custom-item-with-catalog-reference).
   * @targetRemovalDate 2026-05-01
   */
  id?: string | null;
  /**
   * Tax group ID for this custom line item.
   * @format GUID
   */
  taxGroupId?: string | null;
  /** Name of the item or product. */
  productName?: ProductName;
  /** URL to the item's page on the site. When not provided, the link back from the cart page to the relevant product page will not work. */
  url?: PageUrlV2;
  /** Item type. Either a preset type or custom. */
  itemType?: ItemType;
  /**
   * Item price **before** catalog-defined discount. Defaults to `price` when not provided.
   * @format DECIMAL_VALUE
   */
  fullPrice?: string | null;
  /**
   * Item quantity available for purchase. Only return this if inventory is managed.
   * Not returning this field means that the buyer can "infinitely" tick up the number of items in the cart.
   * @max 100000
   */
  quantityAvailable?: number | null;
  /** Physical properties of the item. */
  physicalProperties?: PhysicalProperties;
  /**
   * Type of selected payment option for current item. Defaults to `FULL_PAYMENT_ONLINE`.
   *
   * + `FULL_PAYMENT_ONLINE` - Entire payment for this item happens as part of the checkout.
   * + `FULL_PAYMENT_OFFLINE` - Entire payment for this item happens after the checkout. For example, when using cash, check, or other offline payment methods.
   * + `MEMBERSHIP` - Payment for this item is done by charging a membership. When this option is used, `lineItem.price.amount` is 0.
   * + `DEPOSIT_ONLINE` -  Partial payment for the given item to be paid upfront during the checkout. Amount to be paid is defined by deposit_amount field.
   */
  paymentOption?: PaymentOptionTypeWithLiterals;
  /**
   * Service properties. When relevant, this contains information such as date and number of participants.
   * Used, among other things, when checking for valid memberships.
   */
  serviceProperties?: ServiceProperties;
  /**
   * In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.
   *
   * + For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.
   * + In most cases, this field is the same as `catalogReference.catalogItemId`.
   * + Used in membership validation.
   * @minLength 1
   * @maxLength 36
   */
  rootCatalogItemId?: string | null;
  /**
   * Partial payment for the given item to be paid upfront during the checkout.
   *
   * Eligible for catalog items with type `DEPOSIT_ONLINE`.
   * When omitted, the item's price will not be split and is expected to be paid in a single installment.
   * @format DECIMAL_VALUE
   */
  depositAmount?: string | null;
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId?: string | null;
  /** Catalog and item reference. Includes IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin. */
  catalogReference?: CatalogReference;
  /**
   * Whether the price is not yet defined, and will be updated after the order is created.
   *
   * Default: `false`
   */
  priceUndetermined?: boolean;
  /**
   * Whether the line item quantity is fixed and cannot be changed.
   *
   * Default: `false`
   */
  fixedQuantity?: boolean;
  /**
   * Item payment policy that requires customer consent to complete purchase. The payment policy will be displayed on the checkout page.
   * @minLength 1
   * @maxLength 1000
   * @readonly
   */
  consentRequiredPaymentPolicy?: string | null;
  /**
   * Whether to save the payment method on the order.
   *
   * Default: `false`
   * @readonly
   */
  savePaymentMethod?: boolean;
  /**
   * Policies to be displayed to the customer on the checkout page.
   * @maxSize 5
   */
  policies?: Policy[];
  /**
   * Whether the item can only be purchased by site members.
   *
   * Default: `false`
   */
  membersOnly?: boolean;
  /** Address to use for tax calculation purposes. */
  taxableAddress?: TaxableAddress;
  /**
   * Modifier groups that were added to the item.
   * @maxSize 25
   */
  modifierGroups?: V1ModifierGroup[];
}

export interface PriceDescription {
  /**
   * __Required.__ Price description in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @minLength 1
   * @maxLength 100
   */
  original?: string;
  /**
   * Price description translated into the buyer's language.
   *
   * Default: Same as `original`.
   * @minLength 1
   * @maxLength 100
   */
  translated?: string | null;
}

export interface PageUrlV2 {
  /** The relative path for the page within the site. For example, `/product-page/a-product`. */
  relativePath?: string;
  /** The page's full URL. For example, `https://mysite.com/product-page/a-product`. */
  url?: string | null;
}

export interface ItemType extends ItemTypeItemTypeDataOneOf {
  /** Preset item type. */
  preset?: ItemTypePresetWithLiterals;
  /** Custom item type. When none of the preset types are suitable, specifies the custom type. */
  custom?: string;
}

/** @oneof */
export interface ItemTypeItemTypeDataOneOf {
  /** Preset item type. */
  preset?: ItemTypePresetWithLiterals;
  /** Custom item type. When none of the preset types are suitable, specifies the custom type. */
  custom?: string;
}

export enum ItemTypePreset {
  UNRECOGNISED = 'UNRECOGNISED',
  PHYSICAL = 'PHYSICAL',
  DIGITAL = 'DIGITAL',
  GIFT_CARD = 'GIFT_CARD',
  SERVICE = 'SERVICE',
}

/** @enumType */
export type ItemTypePresetWithLiterals =
  | ItemTypePreset
  | 'UNRECOGNISED'
  | 'PHYSICAL'
  | 'DIGITAL'
  | 'GIFT_CARD'
  | 'SERVICE';

export interface SubscriptionOptionInfo {
  /** Subscription option settings. */
  subscriptionSettings?: SubscriptionSettings;
  /** Subscription option title. */
  title?: Title;
  /** Subscription option description. */
  description?: Description;
}

export interface SubscriptionSettings {
  /** Frequency of recurring payment. */
  frequency?: SubscriptionFrequencyWithLiterals;
  /**
   * Interval of recurring payment.
   *
   * Default: `1`.
   * If SubscriptionFrequency is Day the minimum interval is 7
   * @min 1
   * @max 3650
   */
  interval?: number | null;
  /** Whether subscription is renewed automatically at the end of each period. */
  autoRenewal?: boolean;
  /**
   * Number of billing cycles before subscription ends. Ignored if `autoRenewal` is `true`.
   * @min 1
   */
  billingCycles?: number | null;
  /** Whether to allow the customer to cancel the subscription.. */
  enableCustomerCancellation?: boolean;
  /**
   * Period until first cycle starts. If applied payNow will be 0
   * If None => no free trial
   */
  freeTrialPeriod?: FreeTrialPeriod;
}

/** Frequency unit of recurring payment */
export enum SubscriptionFrequency {
  UNDEFINED = 'UNDEFINED',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

/** @enumType */
export type SubscriptionFrequencyWithLiterals =
  | SubscriptionFrequency
  | 'UNDEFINED'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface FreeTrialPeriod {
  /** Frequency of period. Values: DAY, WEEK, MONTH, YEAR */
  frequency?: SubscriptionFrequencyWithLiterals;
  /**
   * Number of frequency units in the free trial period. For example, frequency `MONTH` with interval `3` means a 3-month free trial.
   * @min 1
   * @max 999
   */
  interval?: number;
}

export interface BillingSettings {
  /**
   * Day of month (1-28) to anchor recurring billing to. When set, billing recurs on this day each cycle. Does not apply to weekly subscriptions.
   * @min 1
   * @max 28
   */
  anchorDay?: number | null;
}

export interface Title {
  /**
   * Subscription option name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @minLength 1
   * @maxLength 150
   */
  original?: string;
  /**
   * Subscription option name translated into the buyer's language.
   *
   * Default: Same as `original`.
   * @minLength 1
   * @maxLength 150
   */
  translated?: string | null;
}

export interface Description {
  /**
   * Subscription option description.
   * @maxLength 500
   */
  original?: string;
  /**
   * Translated subscription option description.
   * @maxLength 500
   */
  translated?: string | null;
}

export interface SecuredMedia {
  /**
   * Media ID in Wix Media Manager.
   * @minLength 1
   * @maxLength 100
   */
  id?: string;
  /**
   * Original filename.
   * @minLength 1
   * @maxLength 1000
   */
  fileName?: string;
  /** File type. */
  fileType?: FileTypeWithLiterals;
}

export enum FileType {
  UNSPECIFIED = 'UNSPECIFIED',
  SECURE_PICTURE = 'SECURE_PICTURE',
  SECURE_VIDEO = 'SECURE_VIDEO',
  SECURE_DOCUMENT = 'SECURE_DOCUMENT',
  SECURE_MUSIC = 'SECURE_MUSIC',
  SECURE_ARCHIVE = 'SECURE_ARCHIVE',
  SECURE_RAW = 'SECURE_RAW',
}

/** @enumType */
export type FileTypeWithLiterals =
  | FileType
  | 'UNSPECIFIED'
  | 'SECURE_PICTURE'
  | 'SECURE_VIDEO'
  | 'SECURE_DOCUMENT'
  | 'SECURE_MUSIC'
  | 'SECURE_ARCHIVE'
  | 'SECURE_RAW';

export interface ServiceProperties {
  /**
   * Date and time the service is to be provided, in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#Combined_date_and_time_representations) format.
   * For example, the start time of a class.
   */
  scheduledDate?: Date | null;
  /**
   * The number of people participating in the service. For example, the number of people attending a class or the number of people per hotel room.
   * @min 1
   * @max 10000
   */
  numberOfParticipants?: number | null;
}

export interface Policy {
  /**
   * Policy title - should be translated
   * @minLength 1
   * @maxLength 29
   */
  title?: string | null;
  /**
   * Policy content - should be translated
   * @minLength 1
   * @maxLength 3000
   */
  content?: string;
}

export interface TaxableAddress extends TaxableAddressTaxableAddressDataOneOf {
  /** taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly. */
  addressType?: TaxableAddressTypeWithLiterals;
}

/** @oneof */
export interface TaxableAddressTaxableAddressDataOneOf {
  /** taxable address type. if this field is selected, the address is automatically resolved, and the tax is calculated accordingly. */
  addressType?: TaxableAddressTypeWithLiterals;
}

export enum TaxableAddressType {
  BUSINESS = 'BUSINESS',
  BILLING = 'BILLING',
  SHIPPING = 'SHIPPING',
}

/** @enumType */
export type TaxableAddressTypeWithLiterals =
  | TaxableAddressType
  | 'BUSINESS'
  | 'BILLING'
  | 'SHIPPING';

export interface V1ModifierGroup {
  /**
   * Modifier group ID.
   * @minLength 1
   * @maxLength 36
   */
  id?: string;
  /** Modifier group name. */
  name?: TranslatableString;
  /**
   * List of modifiers in this group.
   * @minSize 1
   * @maxSize 25
   */
  modifiers?: V1ItemModifier[];
}

export interface TranslatableString {
  /**
   * __Required.__ String in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   *
   * Min: 1 character.
   * Max: 200 characters.
   * @minLength 1
   * @maxLength 200
   */
  original?: string;
  /**
   * String translated into the buyer's language.
   *
   * Min: 1 character.
   * Max: 400 characters.
   * Default: Same as `original`.
   * @minLength 1
   * @maxLength 400
   */
  translated?: string | null;
}

export interface V1ItemModifier {
  /**
   * Modifier ID.
   * @minLength 1
   * @maxLength 36
   */
  id?: string;
  /**
   * The quantity of this modifier.
   * @min 1
   * @max 100000
   */
  quantity?: number | null;
  /** Primary display label for the modifier. */
  label?: TranslatableString;
  /** Additional details. */
  details?: TranslatableString;
  /**
   * The price of the modifier.
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:3 }
   */
  price?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CustomContentReference {
  /**
   * ID of the app providing the content.
   *
   * You can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   * @format GUID
   */
  appId?: string;
  /**
   * ID of the component within the app it belongs to.
   *
   * You can get your component's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   * @format GUID
   */
  componentId?: string;
}

export interface ExternalReference {
  /**
   * ID of the app associated with the purchase flow.
   * For example, the Wix Pay Links app ID.
   * @format GUID
   * @immutable
   */
  appId?: string;
  /**
   * Reference to an external resource ID. Used to link the purchase flow to a specific entity in an external system.
   * For example, a Wix Pay Link ID.
   * @minLength 1
   * @maxLength 100
   * @immutable
   */
  resourceId?: string | null;
}

/** Triggered when newly checkout created successfully from checkout template */
export interface CheckoutTemplateUsed {
  /** checkout template */
  checkoutTemplate?: CheckoutTemplate;
  /** newly created checkout */
  checkout?: Checkout;
}

/** A checkout represents a cart that is in the process of being purchased. It holds calculated prices, tax, shipping, and buyer information needed to complete the purchase. */
export interface Checkout {
  /**
   * Checkout ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Line items.
   *
   * Max: 300 items
   * @minSize 1
   * @maxSize 300
   * @readonly
   */
  lineItems?: LineItem[];
  /** Billing information. */
  billingInfo?: AddressWithContact;
  /** Shipping information. */
  shippingInfo?: ShippingInfo;
  /**
   * [Buyer note](https://support.wix.com/en/article/collecting-and-viewing-buyer-notes) left by the customer.
   * @maxLength 1000
   */
  buyerNote?: string | null;
  /** Buyer information. */
  buyerInfo?: BuyerInfo;
  /**
   * The currency [selected by the customer](https://support.wix.com/en/article/multicurrency-an-overview) during the purchase flow, in three-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   *
   * This reflects the customer’s preferred display currency and may differ from the site’s default currency.
   * When no specific currency is selected by the customer, this matches the `currency` property.
   * @readonly
   * @format CURRENCY
   */
  conversionCurrency?: string;
  /**
   * Calculated price summary for the checkout.
   * @readonly
   */
  priceSummary?: PriceSummary;
  /**
   * Errors when calculating totals.
   * @readonly
   */
  calculationErrors?: CalculationErrors;
  /**
   * Applied gift card details.
   *
   * >**Note:** Gift cards are supported through the Wix UI, though the service plugin is not currently available. Learn more about [Wix Gift Cards](https://support.wix.com/en/article/setting-up-wix-gift-cards-9451439).
   * @readonly
   */
  giftCard?: GiftCard;
  /**
   * Applied discounts.
   * @readonly
   */
  appliedDiscounts?: AppliedDiscount[];
  /** Custom fields. */
  customFields?: CustomField[];
  /**
   * Weight measurement unit. Defaults to a site's weight unit.
   * @readonly
   */
  weightUnit?: WeightUnitWithLiterals;
  /**
   * Tax summary.
   * @readonly
   */
  taxSummary?: TaxSummary;
  /**
   * The site’s default currency, in three-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. This represents the base currency configured for the site and remains constant regardless of the customer’s currency selection.
   * @format CURRENCY
   * @readonly
   */
  currency?: string;
  /**
   * Sales channel that submitted the order.
   * @readonly
   */
  channelType?: ChannelTypeWithLiterals;
  /**
   * Site language in which original values are shown.
   * @readonly
   */
  siteLanguage?: string;
  /**
   * Language for communication with the buyer. Defaults to the site language.
   *
   * For a site that supports multiple languages, this is the language the buyer selected.
   * @readonly
   */
  buyerLanguage?: string;
  /**
   * Whether an order was successfully created from this checkout.
   *
   * For an order to be successful, it must be successfully paid for (unless the total is 0).
   * @readonly
   */
  completed?: boolean;
  /**
   * Whether tax is included in line item prices.
   * @readonly
   */
  taxIncludedInPrice?: boolean;
  /**
   * ID of the checkout's initiator.
   * @readonly
   */
  createdBy?: CreatedBy;
  /**
   * Date and time the checkout was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the checkout was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Minimal amount to pay in order to place the order.
   * @readonly
   */
  payNow?: PriceSummary;
  /**
   * Remaining amount for the order to be fully paid.
   * @readonly
   */
  payLater?: PriceSummary;
  /** Memberships to apply when creating the order. */
  membershipOptions?: MembershipOptions;
  /**
   * Additional Fees.
   * @maxSize 100
   * @readonly
   */
  additionalFees?: AdditionalFee[];
  /**
   * Cart ID that this checkout was created from. Empty if this checkout wasn't created from a cart.
   * @format GUID
   */
  cartId?: string | null;
  /**
   * List of validation violations raised by the [Validations service plugin](https://dev.wix.com/docs/rest/business-solutions/e-commerce/service-plugins/validations-integration-service-plugin/introduction).
   * @readonly
   */
  violations?: Violation[];
  /**
   * Custom field data for the checkout object.
   *
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.
   * @format GUID
   * @readonly
   */
  purchaseFlowId?: string | null;
  /**
   * Additional settings for customization of the checkout process.
   *
   * > **Notes:**
   * > * Custom settings can only be set when creating a checkout.
   * > * To access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions". Learn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).
   * @immutable
   */
  customSettings?: CustomSettings;
  /**
   * Reference IDs for the app and component providing custom checkout page content.
   *
   * To access and manage custom checkout page content, your app must have the permission scope named "Manage eCommerce - Admin Permissions".
   * Learn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).
   */
  customContentReference?: CustomContentReference;
  /**
   * References to an external app and resource associated with this checkout.
   * Used for integration and tracking across different platforms.
   * @immutable
   */
  externalReference?: ExternalReference;
  /**
   * Payment for subscriptions after free trial period.
   * @readonly
   */
  payAfterFreeTrial?: PriceSummary;
  /**
   * The business location ID associated with the checkout.
   *
   * To learn more, see the Locations API.
   * @format GUID
   */
  businessLocationId?: string | null;
  /**
   * The currency used for payment, in three-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   *
   * This is determined by the [customer's selected currency](https://support.wix.com/en/article/multicurrency-an-overview) and the site’s supported payment currencies.
   * If the customer’s selected currency is supported for payment, this matches the `conversionCurrency` property. If not supported, this falls back to the `currency` property.
   * @readonly
   * @format CURRENCY
   */
  paymentCurrency?: string;
  /**
   * Tax exempt group ID to use for tax exemptions.
   * @format GUID
   */
  taxExemptGroupId?: string | null;
}

export interface LineItem {
  /**
   * Line item ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Item quantity.
   *
   * Min: `"1"`
   * Max: `"100000"`
   * @min 1
   * @max 100000
   */
  quantity?: number;
  /** Catalog and item reference. Includes IDs for the item and the catalog it came from, as well as further optional info. Optional for custom line items, which don't trigger the Catalog service plugin. */
  catalogReference?: CatalogReference;
  /**
   * Item name.
   * + Stores - `product.name`
   * + Bookings - `service.info.name`
   * + Events - `ticket.name`
   * @readonly
   */
  productName?: ProductName;
  /**
   * URL to the item's page on the site.
   * @readonly
   */
  url?: PageUrlV2;
  /**
   * Item price **after** catalog-defined discount and line item discounts.
   * @readonly
   */
  price?: MultiCurrencyPrice;
  /**
   * Total line item price **after** catalog-defined discount and line item discounts.
   * @readonly
   */
  lineItemPrice?: MultiCurrencyPrice;
  /**
   * Item price **before** catalog-defined discount. Defaults to `price` when not provided.
   * @readonly
   */
  fullPrice?: MultiCurrencyPrice;
  /**
   * Item price **before** line item discounts and **after** catalog-defined discount. Defaults to `price` when not provided.
   * @readonly
   */
  priceBeforeDiscounts?: MultiCurrencyPrice;
  /**
   * Total price after all discounts and tax.
   * @readonly
   */
  totalPriceAfterTax?: MultiCurrencyPrice;
  /**
   * Total price after discounts, and before tax.
   * @readonly
   */
  totalPriceBeforeTax?: MultiCurrencyPrice;
  /**
   * Tax details for this line item.
   * @readonly
   */
  taxDetails?: ItemTaxFullDetails;
  /**
   * Discount for this line item's entire quantity.
   * @readonly
   */
  discount?: MultiCurrencyPrice;
  /**
   * Line item description lines. Used for display purposes for the cart, checkout and order.
   * @readonly
   */
  descriptionLines?: DescriptionLine[];
  /**
   * Line item image details.
   * @readonly
   */
  media?: CommonImage;
  /**
   * Item availability details.
   * @readonly
   */
  availability?: ItemAvailabilityInfo;
  /**
   * Physical properties of the item. When relevant, contains information such as SKU, item weight, and shippability.
   * @readonly
   */
  physicalProperties?: PhysicalProperties;
  /**
   * Item type. Either a preset type or custom.
   * @readonly
   */
  itemType?: ItemType;
  /**
   * Subscription option information.
   * @readonly
   */
  subscriptionOptionInfo?: SubscriptionOptionInfo;
  /**
   * Type of selected payment option for current item.
   *
   * Default: `FULL_PAYMENT_ONLINE`
   * @readonly
   */
  paymentOption?: PaymentOptionTypeWithLiterals;
  /**
   * Service properties. When relevant, this contains information such as date and number of participants.
   * @readonly
   */
  serviceProperties?: ServiceProperties;
  /**
   * In cases where `catalogReference.catalogItemId` is NOT the actual catalog item ID, this field will return the true item's ID.
   * + For example, for Wix Bookings, `catalogReference.catalogItemId` is the booking ID. Therefore this value is set to the service ID.
   * + In most cases, this field has the same value as `catalogReference.catalogItemId`.
   * + Used in membership validation.
   * @minLength 1
   * @maxLength 36
   * @readonly
   */
  rootCatalogItemId?: string | null;
  /**
   * Additional description for the price. For example, when price is 0 but additional details about the actual price are needed - "Starts at $67".
   * @readonly
   */
  priceDescription?: PriceDescription;
  /**
   * Partial payment to be paid upfront during the checkout. Eligible for catalog items with `lineItem.paymentOption` type `DEPOSIT_ONLINE` only.
   * @readonly
   */
  depositAmount?: MultiCurrencyPrice;
  /**
   * Delivery profile ID.
   * @format GUID
   * @readonly
   */
  deliveryProfileId?: string | null;
  /**
   * Whether the line item is a custom line item. Custom line items don't trigger the Catalog service plugin.
   * @readonly
   */
  customLineItem?: boolean;
  /**
   * Item payment policy that requires customer consent to complete purchase. The payment policy will be displayed on the checkout page.
   * @minLength 1
   * @maxLength 2500
   * @readonly
   */
  consentRequiredPaymentPolicy?: string | null;
  /**
   * Overriding values for catalog item properties.
   *
   * To override catalog fields, your app must have the permission scope named "Manage eCommerce - Admin Permissions".
   * Learn more about [permission scopes](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/about-permissions).
   */
  catalogOverrideFields?: CatalogOverrideFields;
  /**
   * Whether the price is not yet defined, and will be updated after the order is created.
   * @readonly
   */
  priceUndetermined?: boolean;
  /**
   * Whether the line item quantity is fixed and cannot be changed.
   * @readonly
   */
  fixedQuantity?: boolean;
  /**
   * Whether to save the payment method on the order.
   *
   * Default: `false`
   * @readonly
   */
  savePaymentMethod?: boolean;
  /** Address to use for tax calculation purposes. */
  taxableAddress?: TaxableAddress;
  /**
   * Custom extended fields for the line item object.
   *
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured using the 'Checkout & Orders - Line Items Schema Plugin' in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * Policies to be displayed to the customer on the checkout page.
   * @readonly
   * @maxSize 5
   */
  policies?: Policy[];
  /**
   * Whether the item can only be purchased by site members.
   *
   * Default: `false`
   * @readonly
   */
  membersOnly?: boolean;
  /**
   * Modifier groups that were added to the item.
   * @readonly
   * @maxSize 25
   */
  modifierGroups?: ModifierGroup[];
  /**
   * Total price of all item modifiers. Included in the item price.
   * @readonly
   */
  modifiersTotalPrice?: MultiCurrencyPrice;
}

export interface MultiCurrencyPrice {
  /**
   * Amount.
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  amount?: string;
  /**
   * Converted amount.
   * @readonly
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  convertedAmount?: string;
  /**
   * Amount formatted with currency symbol.
   * @readonly
   */
  formattedAmount?: string;
  /**
   * Converted amount formatted with currency symbol.
   * @readonly
   */
  formattedConvertedAmount?: string;
}

export interface ItemTaxFullDetails {
  /** Amount for which tax is calculated. */
  taxableAmount?: MultiCurrencyPrice;
  /**
   * Tax rate %, as a decimal point between 0 and 1.
   * @decimalValue options { gte:0, lte:1, maxScale:6 }
   */
  taxRate?: string;
  /** Calculated tax, based on `taxable_amount` and `tax_rate`. */
  totalTax?: MultiCurrencyPrice;
  /**
   * If breakdown exists, the sum of rates in the breakdown must equal `tax_rate`. Deprecated - use 'tax_breakdown' instead.
   * @readonly
   * @deprecated
   */
  rateBreakdown?: TaxRateBreakdown[];
  /**
   * tax information for a line item.
   * @maxSize 1000
   * @readonly
   */
  taxBreakdown?: TaxBreakdown[];
}

export interface TaxRateBreakdown {
  /**
   * Name of tax against which the calculation was performed.
   * @maxLength 100
   */
  name?: string;
  /**
   * Rate at which this tax detail was calculated.
   * @decimalValue options { gte:0, lte:1, maxScale:6 }
   */
  rate?: string;
  /** Amount of tax for this tax detail. */
  tax?: MultiCurrencyPrice;
}

/**
 * TaxBreakdown represents tax information for a line item.
 * It holds the tax amount and the tax rate for each tax authority that apply on the line item.
 */
export interface TaxBreakdown {
  /**
   * The name of the jurisdiction to which this tax detail applies. For example, "New York" or "Quebec".
   * @maxLength 200
   */
  jurisdiction?: string | null;
  /** The amount of this line item price that was considered nontaxable. (Decimal value) */
  nonTaxableAmount?: MultiCurrencyPrice;
  /**
   * The rate at which this tax detail was calculated, e.g 0.1000 signifies 10% tax and 2.0000 signifies 200% tax. (Decimal value)
   * @decimalValue options { gte:0, maxScale:6 }
   */
  rate?: string | null;
  /** The amount of tax estimated for this line item. (Decimal value) */
  taxAmount?: MultiCurrencyPrice;
  /** The taxable amount of this line item. */
  taxableAmount?: MultiCurrencyPrice;
  /**
   * The type of tax that was calculated. Depends on the jurisdiction's tax laws. For example, "Sales Tax", "Income Tax", "Value Added Tax", etc.
   * @maxLength 200
   */
  taxType?: string | null;
  /**
   * The name of the tax against which this tax amount was calculated. For example, "NY State Sales Tax", "Quebec GST", etc.
   * This name should be explicit enough to allow the merchant to understand what tax was calculated.
   * @maxLength 200
   */
  taxName?: string | null;
  /** The type of the jurisdiction in which this tax detail applies. */
  jurisdictionType?: JurisdictionTypeWithLiterals;
}

/** JurisdictionType represents the type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special). */
export enum JurisdictionType {
  UNDEFINED = 'UNDEFINED',
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
  COUNTY = 'COUNTY',
  CITY = 'CITY',
  SPECIAL = 'SPECIAL',
}

/** @enumType */
export type JurisdictionTypeWithLiterals =
  | JurisdictionType
  | 'UNDEFINED'
  | 'COUNTRY'
  | 'STATE'
  | 'COUNTY'
  | 'CITY'
  | 'SPECIAL';

export interface CommonImage {
  /** WixMedia image ID. */
  id?: string;
  /** Image URL. */
  url?: string;
  /**
   * Original image height.
   * @readonly
   */
  height?: number;
  /**
   * Original image width.
   * @readonly
   */
  width?: number;
  /** Image alt text. */
  altText?: string | null;
  /**
   * Image filename.
   * @readonly
   */
  filename?: string | null;
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
  /** crop by height */
  height?: number | null;
  /** crop by width */
  width?: number | null;
}

export interface ItemAvailabilityInfo {
  /** Item availability status. */
  status?: ItemAvailabilityStatusWithLiterals;
  /** Quantity available. */
  quantityAvailable?: number | null;
}

export enum ItemAvailabilityStatus {
  AVAILABLE = 'AVAILABLE',
  /** Item does not exist. */
  NOT_FOUND = 'NOT_FOUND',
  /** Item is not in stock. */
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  /** Available quantity is less than requested. */
  PARTIALLY_AVAILABLE = 'PARTIALLY_AVAILABLE',
}

/** @enumType */
export type ItemAvailabilityStatusWithLiterals =
  | ItemAvailabilityStatus
  | 'AVAILABLE'
  | 'NOT_FOUND'
  | 'NOT_AVAILABLE'
  | 'PARTIALLY_AVAILABLE';

export interface Scope {
  /** Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans) */
  namespace?: string;
  /** Coupon scope's applied group (e.g., event or ticket in Wix Events) */
  group?: Group;
}

export interface Group {
  /** Coupon scope's group (e.g., product or collection in Wix Stores). See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values). */
  name?: string;
  /** Item ID (when the coupon scope is limited to just one item). */
  entityId?: string | null;
}

export interface ModifierGroup {
  /**
   * Modifier group ID.
   * @minLength 1
   * @maxLength 36
   */
  id?: string;
  /** Modifier group name. */
  name?: TranslatableString;
  /**
   * List of modifiers in this group.
   * @minSize 1
   * @maxSize 25
   */
  modifiers?: ItemModifier[];
}

export interface ItemModifier {
  /**
   * Modifier ID.
   * @minLength 1
   * @maxLength 36
   */
  id?: string;
  /**
   * The quantity of this modifier.
   * @min 1
   * @max 100000
   */
  quantity?: number | null;
  /** Primary display label for the modifier. */
  label?: TranslatableString;
  /** Additional details. */
  details?: TranslatableString;
  /** The price of the modifier. */
  price?: MultiCurrencyPrice;
}

export interface PlatformFee {
  /** __Required.__ Platform fee name. */
  name?: TranslatableString;
  /** Platform fee charge type. */
  chargeType?: PlatformFeeChargeTypeWithLiterals;
  /**
   * Percentage rate charged as platform fee.
   *
   * The fee rate percentage expressed as a decimal fraction between 0 and 1. For example, `0.05` for 5%.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, lte:1, maxScale:4 }
   */
  percentageRate?: string;
}

export enum PlatformFeeChargeType {
  /** The platform fee is passed on to the customer. */
  PASS_ON = 'PASS_ON',
  /** The platform fee is absorbed by the merchant. */
  ABSORBED = 'ABSORBED',
}

/** @enumType */
export type PlatformFeeChargeTypeWithLiterals =
  | PlatformFeeChargeType
  | 'PASS_ON'
  | 'ABSORBED';

/** Billing Info and shipping details */
export interface AddressWithContact {
  /** Address. */
  address?: Address;
  /** Contact details. */
  contactDetails?: FullAddressContactDetails;
}

/** Physical address */
export interface Address {
  /**
   * Two-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.
   * @maxLength 50
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 50
   */
  city?: string | null;
  /**
   * Postal or zip code.
   * @maxLength 50
   */
  postalCode?: string | null;
  /** Street address. */
  streetAddress?: StreetAddress;
  /**
   * Main address line (usually street name and number).
   * @maxLength 150
   */
  addressLine?: string | null;
  /**
   * Free text providing more detailed address info. Usually contains apt, suite, floor.
   * @maxLength 100
   */
  addressLine2?: string | null;
  /** Geocode object containing latitude and longitude coordinates. */
  geocode?: AddressLocation;
  /**
   * Country's full name.
   * @readonly
   */
  countryFullname?: string | null;
  /**
   * Subdivision full-name.
   * @readonly
   */
  subdivisionFullname?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

/** Full contact details for an address */
export interface FullAddressContactDetails {
  /**
   * First name.
   * @maxLength 100
   */
  firstName?: string | null;
  /**
   * Last name.
   * @maxLength 100
   */
  lastName?: string | null;
  /**
   * Phone number.
   * @format PHONE
   */
  phone?: string | null;
  /**
   * Company name.
   * @maxLength 1000
   */
  company?: string | null;
  /** Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed. */
  vatId?: VatId;
}

export interface VatId {
  /** Customer's tax ID. */
  id?: string;
  /**
   * Tax type.
   *
   * Supported values:
   * + `CPF`: for individual tax payers
   * + `CNPJ`: for corporations
   */
  type?: VatTypeWithLiterals;
}

/** tax info types */
export enum VatType {
  UNSPECIFIED = 'UNSPECIFIED',
  /** CPF - for individual tax payers. */
  CPF = 'CPF',
  /** CNPJ - for corporations */
  CNPJ = 'CNPJ',
}

/** @enumType */
export type VatTypeWithLiterals = VatType | 'UNSPECIFIED' | 'CPF' | 'CNPJ';

export interface ShippingInfo {
  /** Shipping address and contact details. */
  shippingDestination?: AddressWithContact;
  /** Selected option out of the options allowed for the `region`. */
  selectedCarrierServiceOption?: SelectedCarrierServiceOption;
  /**
   * Shipping region. Based on the address provided.
   * @readonly
   */
  region?: ShippingRegion;
  /**
   * All carrier options for this shipping rule.
   * @readonly
   */
  carrierServiceOptions?: CarrierServiceOption[];
}

export interface SelectedCarrierServiceOption {
  /**
   * Unique identifier of selected option. For example, "usps_std_overnight".
   * @maxLength 100
   */
  code?: string;
  /**
   * Title of the option, such as USPS Standard Overnight Delivery (in the requested locale).
   * For example, "Standard" or "First-Class Package International".
   * @maxLength 250
   * @readonly
   */
  title?: string;
  /**
   * Delivery logistics.
   * @readonly
   */
  logistics?: DeliveryLogistics;
  /**
   * Shipping costs.
   * @readonly
   */
  cost?: SelectedCarrierServiceOptionPrices;
  /**
   * Were we able to find the requested shipping option, or otherwise we fallback to the default one (the first)
   * @readonly
   */
  requestedShippingOption?: boolean;
  /**
   * Other charges
   * @deprecated Other charges
   * @replacedBy additional_fees
   * @targetRemovalDate 2025-10-01
   */
  otherCharges?: SelectedCarrierServiceOptionOtherCharge[];
  /**
   * This carrier's unique ID
   * @format GUID
   */
  carrierId?: string | null;
  /**
   * Delivery solution allocations to different delivery carriers and delivery regions
   * @maxSize 300
   */
  deliveryAllocations?: DeliveryAllocation[];
  /** If the delivery solution is a partial and doesn't apply to all items. */
  partial?: boolean | null;
}

export interface DeliveryLogistics {
  /**
   * Expected delivery time, in free text. For example, "3-5 business days".
   * @maxLength 500
   */
  deliveryTime?: string | null;
  /**
   * Instructions for caller, e.g for pickup: "Please deliver during opening hours, and please don't park in disabled parking spot".
   * @maxLength 1000
   */
  instructions?: string | null;
  /** Pickup details. */
  pickupDetails?: PickupDetails;
}

export interface PickupDetails {
  /** Pickup address. */
  address?: Address;
  /**
   * Whether the pickup address is that of a business - this may effect tax calculation.
   * @deprecated
   */
  businessLocation?: boolean;
  /** Pickup method */
  pickupMethod?: PickupMethodWithLiterals;
}

export enum PickupMethod {
  STORE_PICKUP = 'STORE_PICKUP',
  PICKUP_POINT = 'PICKUP_POINT',
}

/** @enumType */
export type PickupMethodWithLiterals =
  | PickupMethod
  | 'STORE_PICKUP'
  | 'PICKUP_POINT';

export interface DeliveryTimeSlot {
  /** starting time of the delivery time slot */
  from?: Date | null;
  /** ending time of the delivery time slot */
  to?: Date | null;
}

export interface SelectedCarrierServiceOptionPrices {
  /** Total shipping price, after discount and after tax. */
  totalPriceAfterTax?: MultiCurrencyPrice;
  /** Total price of shipping after discounts (when relevant), and before tax. */
  totalPriceBeforeTax?: MultiCurrencyPrice;
  /** Tax details. */
  taxDetails?: ItemTaxFullDetails;
  /** Shipping discount before tax. */
  totalDiscount?: MultiCurrencyPrice;
  /** Shipping price before discount and before tax. */
  price?: MultiCurrencyPrice;
}

export interface SelectedCarrierServiceOptionOtherCharge {
  /** Type of additional cost. */
  type?: ChargeTypeWithLiterals;
  /**
   * Details of the charge, such as 'Full Coverage Insurance of up to 80% of value of shipment'.
   * @maxLength 200
   */
  details?: string | null;
  /** Price of added charge. */
  cost?: SelectedCarrierServiceOptionPrices;
}

export enum ChargeType {
  HANDLING_FEE = 'HANDLING_FEE',
  INSURANCE = 'INSURANCE',
}

/** @enumType */
export type ChargeTypeWithLiterals = ChargeType | 'HANDLING_FEE' | 'INSURANCE';

export interface DeliveryAllocation {
  /** The delivery option's carrier details, could be multiple if the delivery option is a combination of multiple carriers */
  deliveryCarrier?: Carrier;
  /** The delivery region that are relevant for this delivery solution. */
  deliveryRegion?: Region;
  /** Populated if the delivery solution is a partially supplied by this carrier. */
  applicableLineItems?: ApplicableLineItems;
}

export interface Carrier {
  /**
   * The carrier app id
   * @format GUID
   */
  appId?: string | null;
  /**
   * Unique code that acts as an ID for a shipping rate. For example, `"usps_std_overnight"`.
   * @maxLength 250
   */
  code?: string;
}

export interface Region {
  /**
   * The delivery region id.
   * @format GUID
   */
  id?: string | null;
  /**
   * The delivery region name.
   * @maxLength 100
   */
  name?: string | null;
}

export interface ApplicableLineItems {
  /**
   * Line items that the delivery solution is for.
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  lineItemIds?: string[];
}

export interface ShippingRegion {
  /**
   * Shipping region ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /**
   * Shipping region name.
   * @maxLength 100
   */
  name?: string;
}

export interface CarrierServiceOption {
  /**
   * Carrier ID.
   * @format GUID
   */
  carrierId?: string;
  /** Shipping options offered by this carrier for this request. */
  shippingOptions?: ShippingOption[];
}

export interface ShippingOption {
  /**
   * Unique code of provided shipping option like "usps_std_overnight".
   * For legacy calculators this would be the UUID of the option.
   * @maxLength 100
   */
  code?: string;
  /**
   * Title of the option, such as USPS Standard Overnight Delivery (in the requested locale).
   * For example, "Standard" or "First-Class Package International".
   * @maxLength 250
   */
  title?: string;
  /** Delivery logistics. */
  logistics?: DeliveryLogistics;
  /** Sipping price information. */
  cost?: ShippingPrice;
  /**
   * Delivery solution allocations to different delivery carriers and delivery regions
   * @maxSize 300
   */
  deliveryAllocations?: DeliveryAllocation[];
  /** If the delivery solution is a partial and doesn't apply to all items. */
  partial?: boolean | null;
}

export interface ShippingPrice {
  /** Shipping price. */
  price?: MultiCurrencyPrice;
  /** Other costs such as insurance, handling & packaging for fragile items, etc. */
  otherCharges?: OtherCharge[];
}

export interface OtherCharge {
  /** Type of additional cost. */
  type?: ChargeTypeWithLiterals;
  /** Price of added cost. */
  price?: MultiCurrencyPrice;
  /**
   * Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.
   * @maxLength 250
   */
  details?: string | null;
}

export interface BuyerInfo extends BuyerInfoIdOneOf {
  /**
   * Visitor ID (if site visitor is **not** a member).
   * @format GUID
   * @readonly
   */
  visitorId?: string;
  /**
   * Member ID (if site visitor is a site member).
   * @format GUID
   * @readonly
   */
  memberId?: string;
  /**
   * Contact ID. Auto-created if one does not yet exist. For more information, see the Contacts API.
   * @format GUID
   * @readonly
   */
  contactId?: string | null;
  /**
   * Buyer email address.
   * @format EMAIL
   */
  email?: string | null;
}

/** @oneof */
export interface BuyerInfoIdOneOf {
  /**
   * Visitor ID (if site visitor is **not** a member).
   * @format GUID
   * @readonly
   */
  visitorId?: string;
  /**
   * Member ID (if site visitor is a site member).
   * @format GUID
   * @readonly
   */
  memberId?: string;
}

export interface PriceSummary {
  /** Subtotal of all line items, before discounts and before tax. */
  subtotal?: MultiCurrencyPrice;
  /** Total shipping price, before discounts and before tax. */
  shipping?: MultiCurrencyPrice;
  /** Total tax. */
  tax?: MultiCurrencyPrice;
  /** Total calculated discount value. */
  discount?: MultiCurrencyPrice;
  /** Total price after discounts, gift cards, and tax. */
  total?: MultiCurrencyPrice;
  /** Total additional fees price before tax. */
  additionalFees?: MultiCurrencyPrice;
}

export interface CalculationErrors
  extends CalculationErrorsShippingCalculationErrorOneOf {
  /** General shipping calculation error. */
  generalShippingCalculationError?: Details;
  /** Errors related to the delivery carrier. */
  carrierErrors?: CarrierErrors;
  /** Error related to the selected shipping option. */
  selectedShippingOptionError?: Details;
  /** Tax calculation error. */
  taxCalculationError?: Details;
  /** Coupon calculation error. */
  couponCalculationError?: Details;
  /**
   * DEPRECATED: Use gift_card_calculation_errors instead.
   * @deprecated DEPRECATED: Use gift_card_calculation_errors instead.
   * @replacedBy gift_card_calculation_errors
   * @targetRemovalDate 2026-12-01
   */
  giftCardCalculationError?: Details;
  /** Order validation errors. */
  orderValidationErrors?: ApplicationError[];
  /**
   * Membership payment methods calculation errors
   * For example, will indicate that a line item that must be paid with membership payment doesn't have one or selected memberships are invalid
   */
  membershipError?: Details;
  /** Discount Rule calculation error. */
  discountsCalculationError?: Details;
}

/** @oneof */
export interface CalculationErrorsShippingCalculationErrorOneOf {
  /** General shipping calculation error. */
  generalShippingCalculationError?: Details;
  /** Errors related to the delivery carrier. */
  carrierErrors?: CarrierErrors;
  /** Error related to the selected shipping option. */
  selectedShippingOptionError?: Details;
}

export interface Details extends DetailsKindOneOf {
  applicationError?: ApplicationError;
  validationError?: ValidationError;
  systemError?: SystemError;
  /**
   * deprecated in API's - to enable migration from rendering arbitrary tracing to rest response
   * @deprecated
   */
  tracing?: Record<string, string>;
}

/** @oneof */
export interface DetailsKindOneOf {
  applicationError?: ApplicationError;
  validationError?: ValidationError;
  systemError?: SystemError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

/**
 * example result:
 * {
 * "fieldViolations": [
 * {
 * "field": "fieldA",
 * "description": "invalid music note. supported notes: [do,re,mi,fa,sol,la,ti]",
 * "violatedRule": "OTHER",
 * "ruleName": "INVALID_NOTE",
 * "data": {
 * "value": "FI"
 * }
 * },
 * {
 * "field": "fieldB",
 * "description": "field value out of range. supported range: [0-20]",
 * "violatedRule": "MAX",
 * "data": {
 * "threshold": 20
 * }
 * },
 * {
 * "field": "fieldC",
 * "description": "invalid phone number. provide a valid phone number of size: [7-12], supported characters: [0-9, +, -, (, )]",
 * "violatedRule": "FORMAT",
 * "data": {
 * "type": "PHONE"
 * }
 * }
 * ]
 * }
 */
export interface ValidationError {
  fieldViolations?: FieldViolation[];
}

export enum RuleType {
  VALIDATION = 'VALIDATION',
  OTHER = 'OTHER',
  MAX = 'MAX',
  MIN = 'MIN',
  MAX_LENGTH = 'MAX_LENGTH',
  MIN_LENGTH = 'MIN_LENGTH',
  MAX_SIZE = 'MAX_SIZE',
  MIN_SIZE = 'MIN_SIZE',
  FORMAT = 'FORMAT',
  DECIMAL_LTE = 'DECIMAL_LTE',
  DECIMAL_GTE = 'DECIMAL_GTE',
  DECIMAL_LT = 'DECIMAL_LT',
  DECIMAL_GT = 'DECIMAL_GT',
  DECIMAL_MAX_SCALE = 'DECIMAL_MAX_SCALE',
  INVALID_ENUM_VALUE = 'INVALID_ENUM_VALUE',
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  FIELD_NOT_ALLOWED = 'FIELD_NOT_ALLOWED',
  ONE_OF_ALIGNMENT = 'ONE_OF_ALIGNMENT',
  EXACT_LENGTH = 'EXACT_LENGTH',
  EXACT_SIZE = 'EXACT_SIZE',
  REQUIRED_ONE_OF_FIELD = 'REQUIRED_ONE_OF_FIELD',
}

/** @enumType */
export type RuleTypeWithLiterals =
  | RuleType
  | 'VALIDATION'
  | 'OTHER'
  | 'MAX'
  | 'MIN'
  | 'MAX_LENGTH'
  | 'MIN_LENGTH'
  | 'MAX_SIZE'
  | 'MIN_SIZE'
  | 'FORMAT'
  | 'DECIMAL_LTE'
  | 'DECIMAL_GTE'
  | 'DECIMAL_LT'
  | 'DECIMAL_GT'
  | 'DECIMAL_MAX_SCALE'
  | 'INVALID_ENUM_VALUE'
  | 'REQUIRED_FIELD'
  | 'FIELD_NOT_ALLOWED'
  | 'ONE_OF_ALIGNMENT'
  | 'EXACT_LENGTH'
  | 'EXACT_SIZE'
  | 'REQUIRED_ONE_OF_FIELD';

export interface FieldViolation {
  field?: string;
  description?: string;
  violatedRule?: RuleTypeWithLiterals;
  /** applicable when violated_rule=OTHER */
  ruleName?: string | null;
  data?: Record<string, any> | null;
}

export interface SystemError {
  /** Error code. */
  errorCode?: string | null;
}

export interface CarrierErrors {
  /** Carrier errors. */
  errors?: CarrierError[];
}

export interface CarrierError {
  /** Carrier ID. */
  carrierId?: string;
  /** Error details. */
  error?: Details;
}

export interface GiftCardCalculationError {
  /** Gift card code with most characters masked for security. */
  obfuscatedCode?: string;
  /** Error details. */
  error?: Details;
}

export interface GiftCard {
  /**
   * Gift Card ID.
   * @deprecated
   */
  id?: string;
  /** Gift card obfuscated code. */
  obfuscatedCode?: string;
  /** Actual amount to be redeemed from the gift card. */
  amount?: MultiCurrencyPrice;
  /**
   * App ID of the gift card provider.
   * @format GUID
   */
  appId?: string;
  /**
   * External ID in the gift card provider's system.
   * Used for integration and tracking across different platforms.
   * @minLength 1
   * @maxLength 50
   */
  externalId?: string | null;
  /** Requested amount to redeem from the gift card. */
  requestedAmount?: MultiCurrencyPrice;
}

export interface AppliedDiscount extends AppliedDiscountDiscountSourceOneOf {
  /** Coupon details. */
  coupon?: Coupon;
  /** Merchant discount. */
  merchantDiscount?: MerchantDiscount;
  /** Discount rule */
  discountRule?: DiscountRule;
  /** Discount type. */
  discountType?: DiscountTypeWithLiterals;
  /**
   * IDs of line items the discount applies to.
   * @format GUID
   */
  lineItemIds?: string[];
}

/** @oneof */
export interface AppliedDiscountDiscountSourceOneOf {
  /** Coupon details. */
  coupon?: Coupon;
  /** Merchant discount. */
  merchantDiscount?: MerchantDiscount;
  /** Discount rule */
  discountRule?: DiscountRule;
}

export enum DiscountType {
  GLOBAL = 'GLOBAL',
  SPECIFIC_ITEMS = 'SPECIFIC_ITEMS',
  SHIPPING = 'SHIPPING',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'GLOBAL'
  | 'SPECIFIC_ITEMS'
  | 'SHIPPING';

/** Coupon */
export interface Coupon {
  /** Coupon ID. */
  id?: string;
  /** Coupon code. */
  code?: string;
  /** Coupon value. */
  amount?: MultiCurrencyPrice;
  /** Coupon name. */
  name?: string;
}

export interface MerchantDiscount {
  /** Discount value. */
  amount?: MultiCurrencyPrice;
  /**
   * Discount Percentage. Will be calculated from items price before other discounts.
   * @min 1
   * @max 100
   */
  percentage?: number | null;
}

export interface DiscountRule {
  /**
   * Discount rule ID
   * @format GUID
   */
  id?: string;
  /** Discount rule name */
  name?: DiscountRuleName;
  /** Discount value. */
  amount?: MultiCurrencyPrice;
}

export interface DiscountRuleName {
  /**
   * Original discount rule name (in site's default language).
   * @minLength 1
   * @maxLength 256
   */
  original?: string;
  /**
   * Translated discount rule name according to buyer language. Defaults to `original` when not provided.
   * @minLength 1
   * @maxLength 500
   */
  translated?: string | null;
}

export interface LineItemDiscount {
  /**
   * ID of line item the discount applies to.
   * @format GUID
   */
  id?: string;
  /** Discount value. */
  totalDiscountAmount?: MultiCurrencyPrice;
}

export interface ItemCombination {
  /**
   * The number of times this exact combination of items (with the specified quantities) was applied together in the order.
   * @min 1
   * @max 100000
   */
  count?: number;
  /**
   * Line items that participated together in this combination.
   * @minSize 1
   * @maxSize 100
   */
  lineItems?: ItemCombinationLineItem[];
}

export interface ItemCombinationLineItem {
  /**
   * The unique ID of the line item to which this discount applies.
   * @format GUID
   */
  lineItemId?: string;
  /** Total discount amount for all units (quantity) of this line item in this combination. */
  discountAmount?: MultiCurrencyPrice;
  /**
   * Number of units from this line item that participated in a single combination.
   * @min 1
   * @max 100000
   */
  quantity?: number;
}

export interface CustomField {
  /** Custom field value. */
  value?: any;
  /**
   * Custom field title.
   * @minLength 1
   * @maxLength 500
   */
  title?: string;
  /**
   * Translated custom field title.
   * @minLength 1
   * @maxLength 500
   */
  translatedTitle?: string | null;
}

export enum WeightUnit {
  /** Weight unit can't be classified due to an error. */
  UNSPECIFIED_WEIGHT_UNIT = 'UNSPECIFIED_WEIGHT_UNIT',
  /** Kilograms. */
  KG = 'KG',
  /** Pounds. */
  LB = 'LB',
}

/** @enumType */
export type WeightUnitWithLiterals =
  | WeightUnit
  | 'UNSPECIFIED_WEIGHT_UNIT'
  | 'KG'
  | 'LB';

export interface TaxSummary {
  /**
   * Amount for which tax is calculated, added from line items.
   * @readonly
   */
  taxableAmount?: MultiCurrencyPrice;
  /**
   * Calculated tax, added from line items.
   * @readonly
   */
  totalTax?: MultiCurrencyPrice;
  /**
   * Tax calculator that was active when the order was created.
   * @deprecated
   */
  calculationDetails?: TaxCalculationDetails;
}

export interface TaxCalculationDetails
  extends TaxCalculationDetailsCalculationDetailsOneOf {
  /** Reason the manual calculation was used. */
  manualRateReason?: ManualCalculationReasonWithLiterals;
  /** Details of the fallback rate calculation. */
  autoTaxFallbackDetails?: AutoTaxFallbackCalculationDetails;
  /** Rate calculation type. */
  rateType?: RateTypeWithLiterals;
}

/** @oneof */
export interface TaxCalculationDetailsCalculationDetailsOneOf {
  /** Reason the manual calculation was used. */
  manualRateReason?: ManualCalculationReasonWithLiterals;
  /** Details of the fallback rate calculation. */
  autoTaxFallbackDetails?: AutoTaxFallbackCalculationDetails;
}

export enum RateType {
  /** no tax being collected for this request due to location of purchase */
  NO_TAX_COLLECTED = 'NO_TAX_COLLECTED',
  /** manual rate used for calculation */
  MANUAL_RATE = 'MANUAL_RATE',
  /** autotax rate used for calculation */
  AUTO_RATE = 'AUTO_RATE',
  /** fallback rate used for calculation */
  FALLBACK_RATE = 'FALLBACK_RATE',
}

/** @enumType */
export type RateTypeWithLiterals =
  | RateType
  | 'NO_TAX_COLLECTED'
  | 'MANUAL_RATE'
  | 'AUTO_RATE'
  | 'FALLBACK_RATE';

export enum ManualCalculationReason {
  /** user set calculator in Business Manager to be Manual */
  GLOBAL_SETTING_TO_MANUAL = 'GLOBAL_SETTING_TO_MANUAL',
  /** specific region is on manual even though Global setting is Auto-tax */
  REGION_SETTING_TO_MANUAL = 'REGION_SETTING_TO_MANUAL',
}

/** @enumType */
export type ManualCalculationReasonWithLiterals =
  | ManualCalculationReason
  | 'GLOBAL_SETTING_TO_MANUAL'
  | 'REGION_SETTING_TO_MANUAL';

export interface AutoTaxFallbackCalculationDetails {
  /** reason for fallback */
  fallbackReason?: FallbackReasonWithLiterals;
  /** invalid request (i.e. address), timeout, internal error, license error, and others will be encoded here */
  error?: ApplicationError;
}

export enum FallbackReason {
  /** auto-tax failed to be calculated */
  AUTO_TAX_FAILED = 'AUTO_TAX_FAILED',
  /** auto-tax was temporarily deactivated on a system-level */
  AUTO_TAX_DEACTIVATED = 'AUTO_TAX_DEACTIVATED',
}

/** @enumType */
export type FallbackReasonWithLiterals =
  | FallbackReason
  | 'AUTO_TAX_FAILED'
  | 'AUTO_TAX_DEACTIVATED';

/**
 * The summary of the tax breakdown for all the line items. It will hold for each tax name, the aggregated tax amount paid for it and the tax rate.
 * Tax breakdown is the tax amount split to the tax authorities that applied on the line item.
 */
export interface AggregatedTaxBreakdown {
  /**
   * The name of the tax against which this tax amount was calculated.
   * @maxLength 200
   */
  taxName?: string;
  /**
   * The type of tax that was calculated. Depends on the company's nexus settings as well as the jurisdiction's tax laws.
   * @maxLength 200
   */
  taxType?: string;
  /**
   * The name of the jurisdiction in which this tax detail applies.
   * @maxLength 200
   */
  jurisdiction?: string;
  /** The type of the jurisdiction in which this tax detail applies (e.g. Country,State,County,City,Special). */
  jurisdictionTypeEnum?: JurisdictionTypeWithLiterals;
  /**
   * The rate at which this tax detail was calculated, e.g 0.1000 signifies 10% tax and 2.000 signifies 200% tax. (Decimal value)
   * @decimalValue options { gte:0, maxScale:6 }
   */
  rate?: string;
  /** The sum of all the tax from line items that calculated by the tax identifiers. */
  aggregatedTaxAmount?: MultiCurrencyPrice;
  /** The sum of all the taxable amount from line items for tax identifiers. */
  aggregatedTaxableAmount?: MultiCurrencyPrice;
}

export enum ChannelType {
  /** Unspecified sales channel. This value is not supported. */
  UNSPECIFIED = 'UNSPECIFIED',
  /** A web client. */
  WEB = 'WEB',
  /** [Point of sale solutions](https://support.wix.com/en/wix-mobile-pos-2196395). */
  POS = 'POS',
  /** [eBay shop](https://support.wix.com/en/article/wix-stores-connecting-and-setting-up-an-ebay-shop). */
  EBAY = 'EBAY',
  /** [Amazon shop](https://support.wix.com/en/article/wix-stores-connecting-and-setting-up-an-amazon-shop). */
  AMAZON = 'AMAZON',
  /** Other sales platform. */
  OTHER_PLATFORM = 'OTHER_PLATFORM',
  /** [Wix Owner app](https://support.wix.com/article/wix-owner-app-an-overview). */
  WIX_APP_STORE = 'WIX_APP_STORE',
  /** Wix Invoices app in [your dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Finvoices/settings/general-settings) */
  WIX_INVOICES = 'WIX_INVOICES',
  /** Wix merchant backoffice. */
  BACKOFFICE_MERCHANT = 'BACKOFFICE_MERCHANT',
  /** Wish sales channel. */
  WISH = 'WISH',
  /** [ClassPass sales channel](https://support.wix.com/en/article/wix-bookings-letting-clients-book-your-services-with-classpass). */
  CLASS_PASS = 'CLASS_PASS',
  /** Global-E sales channel. */
  GLOBAL_E = 'GLOBAL_E',
  /** [Facebook shop](https://support.wix.com/en/article/wix-stores-changes-to-facebook-shops). */
  FACEBOOK = 'FACEBOOK',
  /** [Etsy sales channel](https://support.wix.com/en/article/wix-stores-request-adding-etsy-as-a-sales-channel). */
  ETSY = 'ETSY',
  /** [TikTok sales channel](https://support.wix.com/en/article/wix-stores-request-adding-tiktok-as-a-sales-channel). */
  TIKTOK = 'TIKTOK',
  /** [Faire marketplace integration](https://support.wix.com/en/article/wix-stores-creating-a-faire-store-using-the-faire-integration-app). */
  FAIRE_COM = 'FAIRE_COM',
  /** PayPal Agentic Checkout sales channel. */
  PAYPAL_AGENTIC_CHECKOUT = 'PAYPAL_AGENTIC_CHECKOUT',
  /** Stripe Agentic Checkout sales channel. */
  STRIPE_AGENTIC_CHECKOUT = 'STRIPE_AGENTIC_CHECKOUT',
}

/** @enumType */
export type ChannelTypeWithLiterals =
  | ChannelType
  | 'UNSPECIFIED'
  | 'WEB'
  | 'POS'
  | 'EBAY'
  | 'AMAZON'
  | 'OTHER_PLATFORM'
  | 'WIX_APP_STORE'
  | 'WIX_INVOICES'
  | 'BACKOFFICE_MERCHANT'
  | 'WISH'
  | 'CLASS_PASS'
  | 'GLOBAL_E'
  | 'FACEBOOK'
  | 'ETSY'
  | 'TIKTOK'
  | 'FAIRE_COM'
  | 'PAYPAL_AGENTIC_CHECKOUT'
  | 'STRIPE_AGENTIC_CHECKOUT';

export interface CreatedBy extends CreatedByIdOneOf {
  /**
   * User ID. When the order was created by a Wix user on behalf of a buyer.
   * For example, via POS (point of service).
   * @format GUID
   */
  userId?: string;
  /**
   * Member ID. When the order was created by a **logged in** site visitor.
   * @format GUID
   */
  memberId?: string;
  /**
   * Visitor ID. When the order was created by a site visitor that was **not** logged in.
   * @format GUID
   */
  visitorId?: string;
  /**
   * App ID. When the order was created by an external application or Wix service.
   * @format GUID
   */
  appId?: string;
}

/** @oneof */
export interface CreatedByIdOneOf {
  /**
   * User ID. When the order was created by a Wix user on behalf of a buyer.
   * For example, via POS (point of service).
   * @format GUID
   */
  userId?: string;
  /**
   * Member ID. When the order was created by a **logged in** site visitor.
   * @format GUID
   */
  memberId?: string;
  /**
   * Visitor ID. When the order was created by a site visitor that was **not** logged in.
   * @format GUID
   */
  visitorId?: string;
  /**
   * App ID. When the order was created by an external application or Wix service.
   * @format GUID
   */
  appId?: string;
}

/** Reserved for internal use. */
export interface MembershipOptions {
  /**
   * Reserved for internal use.
   * @readonly
   * @maxSize 300
   */
  eligibleMemberships?: Membership[];
  /**
   * Reserved for internal use.
   * @readonly
   * @maxSize 300
   */
  invalidMemberships?: InvalidMembership[];
  /** Selected membership to apply to this checkout. */
  selectedMemberships?: SelectedMemberships;
}

export interface Membership {
  /**
   * Membership ID.
   * @minLength 1
   * @maxLength 100
   */
  id?: string;
  /**
   * ID of the application providing this payment option.
   * @format GUID
   */
  appId?: string;
  /** The name of this membership. */
  name?: MembershipName;
  /**
   * Line item IDs which are "paid" for by this membership.
   * @minSize 1
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  lineItemIds?: string[];
  /** Optional - For a membership that has limited credits, information about credit usage. */
  credits?: MembershipPaymentCredits;
  /** Optional - TMembership expiry date. */
  expirationDate?: Date | null;
  /** Additional data about this membership. */
  additionalData?: Record<string, any> | null;
}

export interface MembershipName {
  /**
   * Membership name.
   * @maxLength 100
   */
  original?: string;
  /**
   * Translated membership name. Defaults to `original` when not provided.
   * @maxLength 100
   */
  translated?: string | null;
}

export interface MembershipPaymentCredits {
  /**
   * Membership's initial value.
   * @min 1
   */
  total?: number;
  /** Membership's remaining value. */
  remaining?: number;
}

export enum BalanceType {
  /** Balance is measured in discrete uses (e.g., sessions, visits). */
  PUNCH_CARD = 'PUNCH_CARD',
  /** Balance is measured in a currency-like value (e.g., points). */
  CREDIT = 'CREDIT',
}

/** @enumType */
export type BalanceTypeWithLiterals = BalanceType | 'PUNCH_CARD' | 'CREDIT';

export interface InvalidMembership {
  /** Membership details. */
  membership?: Membership;
  /**
   * Reason why this membership is invalid and cannot be used.
   * @minLength 1
   * @maxLength 100
   */
  reason?: string;
}

export interface SelectedMemberships {
  /**
   * Selected memberships.
   * @maxSize 300
   */
  memberships?: SelectedMembership[];
}

export interface SelectedMembership {
  /**
   * Membership ID.
   * @minLength 1
   * @maxLength 100
   */
  id?: string;
  /**
   * ID of the app providing this payment option.
   * @format GUID
   */
  appId?: string;
  /**
   * IDs of the line items this membership applies to.
   * @minSize 1
   * @maxSize 300
   * @minLength 1
   * @maxLength 100
   */
  lineItemIds?: string[];
}

export interface AdditionalFee {
  /**
   * Additional fee's unique code (or ID) for future processing.
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
  /**
   * Additional fee's name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /** Additional fee's price. */
  price?: MultiCurrencyPrice;
  /** Tax details. */
  taxDetails?: ItemTaxFullDetails;
  /**
   * Provider's app id.
   * @minLength 1
   * @maxLength 100
   */
  providerAppId?: string | null;
  /** Additional fee's price before tax. */
  priceBeforeTax?: MultiCurrencyPrice;
  /** Additional fee's price after tax. */
  priceAfterTax?: MultiCurrencyPrice;
  /**
   * Optional - Line items associated with this additional fee.
   * If no `lineItemIds` are provided, the fee will be associated with the whole cart/checkout/order.
   * @format GUID
   */
  lineItemIds?: string[];
  /** the source the additional fee was added from */
  source?: AdditionalFeeSourceWithLiterals;
  /**
   * The translated name of the additional fee. The translation language is determined by the `languages` field in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @minLength 1
   * @maxLength 50
   */
  translatedName?: string | null;
}

export enum AdditionalFeeSource {
  UNKNOWN_ADDITIONAL_FEE_SOURCE = 'UNKNOWN_ADDITIONAL_FEE_SOURCE',
  /** The additional fee was added by an additional fee service plugin */
  SERVICE_PLUGIN = 'SERVICE_PLUGIN',
  /** The additional fee was added on the item either via the catalog or on custom line item */
  ITEM = 'ITEM',
  /** The additional fee was added manually on the request */
  MANUAL = 'MANUAL',
  /** The additional fee was added by the shipping provider */
  SHIPPING = 'SHIPPING',
  /** The additional fee was added by a Wix vertical and represents a Wix platform fee */
  PLATFORM = 'PLATFORM',
}

/** @enumType */
export type AdditionalFeeSourceWithLiterals =
  | AdditionalFeeSource
  | 'UNKNOWN_ADDITIONAL_FEE_SOURCE'
  | 'SERVICE_PLUGIN'
  | 'ITEM'
  | 'MANUAL'
  | 'SHIPPING'
  | 'PLATFORM';

export interface ConversionInfo {
  /**
   * The site currency.
   * @readonly
   * @format CURRENCY
   */
  siteCurrency?: string;
  /**
   * The rate used when converting from the site currency to the checkout currency.
   * @readonly
   * @decimalValue options { gt:0, lte:1000000000000000 }
   */
  conversionRate?: string;
}

export interface Violation {
  /** Severity of the violation. The violations are shown on the cart and checkout pages. A warning is displayed as yellow, and allows a site visitor to proceed with caution. An error is displayed as red, and doesn't allow a site visitor to proceed with the eCommerce flow. */
  severity?: SeverityWithLiterals;
  /** Target location on a checkout or cart page where the violation will be displayed. */
  target?: Target;
  /**
   * Violation description. Can include rich text. Only HTTP or HTTPS links in the following format are allowed: `<a href="https://www.wix.com">Click me</a>`.
   * @minLength 1
   * @maxLength 1000
   */
  description?: string | null;
}

export enum Severity {
  /** The user is allowed to move forward in the flow. */
  WARNING = 'WARNING',
  /**
   * The user is blocked from moving forward in the flow.
   * For example, if callerContext is CART - moving to checkout is blocked. if callerContext is CHECKOUT, placing an order is blocked.
   */
  ERROR = 'ERROR',
}

/** @enumType */
export type SeverityWithLiterals = Severity | 'WARNING' | 'ERROR';

export interface Target extends TargetTargetTypeOneOf {
  /** General (other) violation. */
  other?: Other;
  /** Specific line item violation. */
  lineItem?: TargetLineItem;
}

/** @oneof */
export interface TargetTargetTypeOneOf {
  /** General (other) violation. */
  other?: Other;
  /** Specific line item violation. */
  lineItem?: TargetLineItem;
}

/** Available locations on the webpage */
export enum NameInOther {
  /** Default location, in case no specific location is specified. */
  OTHER_DEFAULT = 'OTHER_DEFAULT',
}

/** @enumType */
export type NameInOtherWithLiterals = NameInOther | 'OTHER_DEFAULT';

/** Available locations on the line item */
export enum NameInLineItem {
  /** Default location, in case no specific location is specified. */
  LINE_ITEM_DEFAULT = 'LINE_ITEM_DEFAULT',
}

/** @enumType */
export type NameInLineItemWithLiterals = NameInLineItem | 'LINE_ITEM_DEFAULT';

export enum SuggestedFix {
  /** No suggested fix is specified. The user should refer to the violation description to resolve the issue. */
  UNKNOWN_SUGGESTED_FIX = 'UNKNOWN_SUGGESTED_FIX',
  /** The line item should be removed from the cart or checkout to resolve the violation. */
  REMOVE_LINE_ITEM = 'REMOVE_LINE_ITEM',
}

/** @enumType */
export type SuggestedFixWithLiterals =
  | SuggestedFix
  | 'UNKNOWN_SUGGESTED_FIX'
  | 'REMOVE_LINE_ITEM';

/** General (other) violation. */
export interface Other {
  /** Location on a checkout or a cart page where a general (other) violation will be displayed. */
  name?: NameInOtherWithLiterals;
}

/** Specific line item violation. */
export interface TargetLineItem {
  /** Location on a checkout or a cart page where the specific line item violation will be displayed. */
  name?: NameInLineItemWithLiterals;
  /** ID of the line item containing the violation. */
  id?: string | null;
}

export interface CustomSettings {
  /**
   * Whether to restrict the option to add or remove a gift card on the checkout page.
   *
   * Default: `false`
   */
  lockGiftCard?: boolean;
  /**
   * Whether to restrict the option to add or remove a coupon code on the checkout page.
   *
   * Default: `false`
   */
  lockCouponCode?: boolean;
  /**
   * Whether to disable policy agreement checkout in the checkout page
   *
   * Default: `false`
   */
  disabledPolicyAgreementCheckbox?: boolean;
  /**
   * Whether to disable manual payment option for this checkout.
   *
   * Default: `false`
   */
  disabledManualPayment?: boolean;
}

export interface SubscriptionCharges {
  /**
   * ids of the items the subscription is defined on
   * @format GUID
   * @minSize 1
   * @maxSize 300
   */
  lineItemIds?: string[];
  /**
   * Generated description explaining future charges amount and schedule.
   * Translated to subscription order buyer language, or to site language if was not provided.
   * @maxLength 1000
   */
  description?: string | null;
  /**
   * Charges for subscription.
   * @maxSize 50
   */
  charges?: Charge[];
}

export interface Charge {
  /** The cycle number from which the charge starts. */
  cycleFrom?: number;
  /** The number of cycles for which the charge is applicable. */
  cycleCount?: number | null;
  /** Charge summary. */
  priceSummary?: PriceSummary;
  /** The billing date from which the charge starts. */
  cycleBillingDate?: Date | null;
}

export interface CreateCheckoutTemplateRequest {
  /** Checkout template to create. */
  checkoutTemplate: CheckoutTemplate;
}

export interface CreateCheckoutTemplateResponse {
  /** Created checkout template. */
  checkoutTemplate?: CheckoutTemplate;
}

export interface GetCheckoutTemplateRequest {
  /**
   * ID of the checkout template to retrieve.
   * @format GUID
   */
  checkoutTemplateId: string;
}

export interface GetCheckoutTemplateResponse {
  /** Retrieved checkout template. */
  checkoutTemplate?: CheckoutTemplate;
}

export interface UpdateCheckoutTemplateRequest {
  /** Checkout template info to update. */
  checkoutTemplate: CheckoutTemplate;
}

export interface UpdateCheckoutTemplateResponse {
  /** Updated checkout template. */
  checkoutTemplate?: CheckoutTemplate;
}

export interface DeleteCheckoutTemplateRequest {
  /**
   * ID of the checkout template to delete.
   * @format GUID
   */
  checkoutTemplateId: string;
}

export interface DeleteCheckoutTemplateResponse {}

export interface QueryCheckoutTemplatesRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   *
   * Example of operators: `$eq`, `$ne`, `$in`, `$startsWith`, `$exists`, `$hasSome`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Ascending order. */
  ASC = 'ASC',
  /** Descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results. </ br>
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryCheckoutTemplatesResponse {
  /** Retrieved checkout templates. */
  checkoutTemplates?: CheckoutTemplate[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to previous page in the list of results. */
  prev?: string | null;
}

export interface CreateCheckoutFromTemplateRequest {
  /**
   * ID of the checkout template to use to create a checkout from.
   * @format GUID
   */
  checkoutTemplateId: string;
  /**
   * ID of the site associated with the checkout template.
   * @format GUID
   */
  siteId: string;
}

export interface CreateCheckoutFromTemplateResponse {
  /**
   * ID of the created checkout.
   * @format GUID
   */
  checkoutId?: string;
  /** URL of the created checkout page. */
  checkoutUrl?: string;
  /**
   * Persistent ID that correlates between the various eCommerce elements: cart, checkout, and order.
   * @format GUID
   */
  purchaseFlowId?: string | null;
}

export interface CreateAndRedirectToCheckoutRequest {
  /**
   * ID of the checkout template to use to create a checkout.
   * @format GUID
   */
  checkoutTemplateId: string;
  /**
   * ID of the site associated with the checkout template.
   * @format GUID
   */
  siteId: string;
}

export interface RawHttpResponse {
  body?: Uint8Array;
  statusCode?: number | null;
  headers?: HeadersEntry[];
}

export interface HeadersEntry {
  key?: string;
  value?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateCheckoutTemplateApplicationErrors = {
  code?: 'CANNOT_CREATE_CHECKOUT_TEMPLATE_WITHOUT_ITEMS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateCheckoutTemplateApplicationErrors =
  | {
      code?: 'CANNOT_UPDATE_CHECKOUT_TEMPLATE_WITHOUT_SPECIFIED_FIELDS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CANNOT_UPDATE_CHECKOUT_TEMPLATE_WITH_UNSUPPORTED_FIELD';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CANNOT_UPDATE_CHECKOUT_UPDATE_TEMPLATE_WITH_UNKNOWN_STATUS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CANNOT_UPDATE_CHECKOUT_TEMPLATE_WITH_EMPTY_ITEMS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CreateCheckoutFromTemplateApplicationErrors =
  | {
      code?: 'CANNOT_CREATE_CHECKOUT_FROM_INACTIVE_TEMPLATE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ITEM_NOT_FOUND_IN_CATALOG';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INSUFFICIENT_INVENTORY';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CreateAndRedirectToCheckoutApplicationErrors = {
  code?: 'CANNOT_CREATE_CHECKOUT_FROM_INACTIVE_TEMPLATE';
  description?: string;
  data?: Record<string, any>;
};
